import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ShieldCheck, Clock, Scale, ArrowRight, ArrowDown } from "lucide-react";
import { scrollToId } from "../hooks/useLenis";

const LINES = ["Dal conflitto", "alla collaborazione"];

export const KineticHero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yEmblem = useTransform(scrollYProgress, [0, 1], [0, 90]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative overflow-hidden pt-32 pb-16 lg:pt-44 lg:pb-28">
      <div className="absolute -top-24 -right-24 w-[480px] h-[480px] rounded-full bg-gold/10 blur-3xl pointer-events-none" />
      <div className="absolute top-1/2 -left-40 w-[380px] h-[380px] rounded-full bg-ink/5 blur-3xl pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center relative">
        <div className="lg:col-span-7">
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="overline-tag mb-6"
            data-testid="hero-overline"
          >
            Mediazione familiare · Crotone (KR)
          </motion.p>

          <h1 data-testid="hero-title" className="font-serif font-semibold text-ink tracking-tight leading-[1.05] text-4xl sm:text-5xl lg:text-6xl">
            {LINES.map((line, i) => (
              <span key={line} className="block overflow-hidden pb-1">
                <motion.span
                  className="block"
                  initial={{ y: "110%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 0.9, delay: 0.15 + i * 0.18, ease: [0.22, 1, 0.36, 1] }}
                >
                  {line}
                </motion.span>
              </span>
            ))}
            <span className="block overflow-hidden pb-2">
              <motion.span
                className="block italic text-golddeep"
                initial={{ y: "110%" }}
                animate={{ y: 0 }}
                transition={{ duration: 0.9, delay: 0.51, ease: [0.22, 1, 0.36, 1] }}
              >
                genitoriale.
              </motion.span>
            </span>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="mt-6 max-w-xl text-base sm:text-lg leading-relaxed text-mist"
          >
            Quando una coppia si separa, il dialogo può sembrare un ponte crollato.
            La mediazione familiare lo ricostruisce: uno spazio neutro e riservato, a Crotone,
            dove ritrovare accordi condivisi mettendo i figli al centro.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.85, ease: [0.22, 1, 0.36, 1] }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <button onClick={() => scrollToId("contatti")} data-testid="hero-cta-contact"
              className="group inline-flex items-center gap-2 rounded-full bg-ink text-paper font-semibold px-7 py-3.5 hover:bg-night hover:-translate-y-0.5 active:scale-95 transition-all duration-300">
              Prenota un colloquio informativo
              <ArrowRight size={17} className="group-hover:translate-x-1 transition-transform duration-300" />
            </button>
            <button onClick={() => scrollToId("manifesto")} data-testid="hero-cta-info"
              className="inline-flex items-center gap-2 rounded-full border border-ink/25 text-ink font-semibold px-7 py-3.5 hover:border-gold hover:text-golddeep transition-colors duration-300">
              Scopri come funziona
              <ArrowDown size={16} />
            </button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.05 }}
            className="mt-10 flex flex-wrap gap-x-8 gap-y-3"
            data-testid="hero-badges"
          >
            {[
              { icon: ShieldCheck, label: "Riservatezza garantita" },
              { icon: Scale, label: "Riconosciuta dalla legge 54/2006" },
              { icon: Clock, label: "Tempi brevi, costi chiari" },
            ].map(({ icon: Icon, label }) => (
              <span key={label} className="inline-flex items-center gap-2 text-sm text-ink/70">
                <Icon size={16} className="text-golddeep" /> {label}
              </span>
            ))}
          </motion.div>
        </div>

        <div className="lg:col-span-5 relative flex justify-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.92 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.1, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="relative"
          >
            <motion.div style={{ y: yEmblem }} className="arch-frame bg-sand border border-ink/10 p-8 sm:p-10 shadow-[0_30px_60px_-30px_rgba(20,41,46,0.35)]">
              <img src="/assets/logo.png" alt="Emblema Oltre il Conflitto: una famiglia attraversa un ponte"
                className="w-56 sm:w-72 object-contain" data-testid="hero-logo-emblem" />
            </motion.div>
            <div className="absolute -top-4 -right-4 w-24 h-24 rounded-full border border-gold/60 pointer-events-none" />
            <div className="absolute -bottom-6 -left-6 w-16 h-16 rounded-full bg-goldlight/80 border border-gold/40 pointer-events-none" />
          </motion.div>
        </div>
      </div>
    </section>
  );
};
